// src/pages/teacher/TeacherStudentResultsDashboard.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Container, Row, Col, Card, Button, Table, Spinner, Alert, Form, InputGroup, Badge, Modal,
} from "react-bootstrap";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import {
  collection, doc, getDoc, getDocs, onSnapshot, query, where, limit as qLimit, setDoc, serverTimestamp
} from "firebase/firestore";
import { ref as sref, getBlob } from "firebase/storage";
import { db, storage } from "../../services/firebase";
import { useAuth } from "../../context/AuthContext";
import duration from "dayjs/plugin/duration";
import { toast } from "react-toastify";

/* ------------------------------------------------
   Small helpers
------------------------------------------------- */
dayjs.extend(duration);
const lower = (s) => String(s || "").trim().toLowerCase();
const norm  = (s) => String(s || "").trim().toLowerCase();
const U     = (s) => String(s || "").trim().toUpperCase();
const toDate = (v) => (v?.toDate ? v.toDate() : v ? new Date(v) : null);
const fmt = (d, f = "DD MMM YYYY, h:mm A") => (d && dayjs(d).isValid() ? dayjs(d).format(f) : "—");
const pickFirst = (...vals) => { for (const v of vals) if (v !== undefined && v !== null && String(v).trim() !== "") return v; return ""; };
const getTestType = (ex) => String(ex?.testType ?? ex?.examTestType ?? ex?.type ?? ex?.category ?? "").trim();

/* normalize teacher sections to an array of UPPERCASE keys */
const parseSections = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.map(String).map(s => s.trim()).filter(Boolean).map(s => s.toUpperCase());
  }
  return String(raw).split(/[,\s/|]+/g).map(s => s.trim()).filter(Boolean).map(s => s.toUpperCase());
};

/* --- attempt/profile extractors (for name/roll/class/section/branch fallbacks) --- */
const extractFromAttempt = (a = {}) => ({
  name:      pickFirst(a.studentName, a.name, a.byStudent?.name, a.byStudent?.fullName),
  roll:      pickFirst(a.roll, a.byStudent?.rollNo, a.byStudent?.roll, a.admissionNo, a.regNo),
  className: pickFirst(a.className, a.byStudent?.className, a.byStudent?.class, a.class, a.grade, a.std),
  section:   pickFirst(a.section, a.byStudent?.section, a.byStudent?.sec),
  branch:    pickFirst(a.branch, a.byStudent?.branch, a.byStudent?.stream, a.byStudent?.department, a.stream, a.department, a.group, a.program),
});

/* --- UI niceties for student avatar --- */
const initialsOf = (name = "") =>
  String(name).trim().split(/\s+/, 2).map(p => p[0]?.toUpperCase() || "").join("") || "ST";
const hueOf = (str = "student") => { let h = 0; for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) % 360; return h; };
const avatarStyle = (name = "") => {
  const h = hueOf(name);
  return { background: `linear-gradient(135deg, hsl(${h},70%,55%), hsl(${(h + 40) % 360},70%,55%))`, color: "#fff" };
};

/* ------------------------------------------------
   Secure PDF viewer (descriptive uploads)
------------------------------------------------- */
function usePdfViewer() {
  const [show, setShow] = useState(false);
  const [objectUrl, setObjectUrl] = useState(null);
  const [title, setTitle] = useState("Answer PDF");

  const openAttemptPdf = useCallback(async (attempt) => {
    const up = attempt?.upload;
    if (!up) return;
    setTitle(up?.name || "Answer PDF");
    try {
      if (up.path) {
        const r = sref(storage, up.path);
        const blob = await getBlob(r);
        const url = URL.createObjectURL(blob);
        setObjectUrl(url);
        setShow(true);
      } else if (up.url) {
        window.open(up.url, "_blank", "noopener");
      }
    } catch {/* ignore */}
  }, []);

  useEffect(() => () => { if (objectUrl) URL.revokeObjectURL(objectUrl); }, [objectUrl]);

  const PdfModal = useMemo(() => function PdfModalComp() {
    if (!show) return null;
    return (
      <div
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1050 }}
        onClick={() => setShow(false)}
      >
        <div className="shadow-lg rounded bg-white" style={{ width: "92vw", height: "85vh" }} onClick={(e) => e.stopPropagation()}>
          <div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
            <div className="fw-semibold">{title}</div>
            <div className="d-flex gap-2">
              {objectUrl && <a href={objectUrl} download={title || "answer.pdf"} className="btn btn-sm btn-primary">Download</a>}
              <Button size="sm" variant="secondary" onClick={() => setShow(false)}>Close</Button>
            </div>
          </div>
          <div style={{ height: "calc(100% - 46px)" }}>
            {objectUrl ? (
              <iframe title="Answer PDF" src={objectUrl} style={{ border: 0, width: "100%", height: "100%" }} />
            ) : (
              <div className="d-flex h-100 w-100 align-items-center justify-content-center"><Spinner /></div>
            )}
          </div>
        </div>
      </div>
    );
  }, [show, objectUrl, title]);

  return { openAttemptPdf, PdfModal };
}

/* ------------------------------------------------
   Visibility check: does an exam target teacher's class/section?
------------------------------------------------- */
function examTargetsTeacher(exam, teacherClass, teacherSectionsU = []) {
  const cls = String(teacherClass || "");
  const sectionsSet = new Set(teacherSectionsU);
  const teacherAllSections = sectionsSet.size === 0;

  if (Array.isArray(exam.targets) && exam.targets.length) {
    return exam.targets.some(t => {
      const secU = U(t.sectionKey || "");
      if (String(t.classId) !== cls) return false;
      if (teacherAllSections) return true;
      return secU === "" || sectionsSet.has(secU);
    });
  }

  if (exam.sectionKeysByClass && exam.sectionKeysByClass[cls]) {
    const arrU = (exam.sectionKeysByClass[cls] || []).map(U);
    if (teacherAllSections) return true;
    return arrU.includes("") || arrU.some(sec => sectionsSet.has(sec));
  }

  if (Array.isArray(exam.classIds) && exam.classIds.includes(cls)) return true;
  return true; // fallback visible
}

/* ------------------------------------------------
   Embedded analysis (modal) — loads one attempt
------------------------------------------------- */
function EmbeddedQuestionAnalysis({ schoolId, examId, uid }) {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [exam, setExam] = useState(null);
  const [attempt, setAttempt] = useState(null);
  const [questions, setQuestions] = useState([]);
  // filters
  const [fSubject, setFSubject] = useState("all");
  const [fTopic, setFTopic] = useState("all");
  const [fType, setFType] = useState("all");
  const [fCorrect, setFCorrect] = useState("all");
  const [q, setQ] = useState("");

  /* ===== helpers (scoped) ===== */
  const toJS = (t) => (t?.toDate ? t.toDate() : t ? new Date(t) : null);
  const nrm = (s) => String(s || "").trim().toLowerCase();

  const readTotal = (obj = {}) => {
    const v = obj.totalMarks ?? obj.maxScore ?? obj.fullMarks ?? obj.total ?? obj.outOf ?? 0;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const getCaseCorrectKey = (sq = {}) => {
    const viaKey = String(sq.correctKey || "").trim().toUpperCase();
    if (viaKey) return viaKey;
    const opt = (sq.options || []).find((o) => o?.isCorrect);
    return opt?.key ? String(opt.key).toUpperCase() : "";
  };
  const subIdFor = (parentQ, sq, idx) => sq?.id || `${parentQ.id}__sub_${idx}`;
  const getAnsValue = (ans) => (ans && typeof ans === "object" && "value" in ans ? ans.value : ans);

  const buildARStemHtml = (q) => {
    const head = q.questionHtml ? `<div>${q.questionHtml}</div>` : "";
    const a = q.assertionHtml || "";
    const r = q.reasonHtml || "";
    return `
      ${head}
      <div><strong>Assertion (A):</strong> ${a || "<em>—</em>"}</div>
      <div><strong>Reason (R):</strong> ${r || "<em>—</em>"}</div>
    `;
  };
  const getMarks = (q) => Number(q.marks ?? q.maxMarks ?? 1) || 1;

  // local evaluator kept stable (no external deps)
  const evaluateAttemptLocally = useCallback((attempt, questions) => {
    const norm = (s) => String(s || "").trim().toLowerCase();
    const getMarks = (q) => Number(q.marks ?? q.maxMarks ?? 1) || 1;
    const getNegMarks = (q) => Math.max(0, Number(q.negativeMarks ?? q.negative ?? q.neg ?? 0) || 0);
    const subIdFor = (parentQ, sq, idx) => sq?.id || `${parentQ.id}__sub_${idx}`;
    const getAnsValue = (ans) => (ans && typeof ans === "object" && "value" in ans ? ans.value : ans);
    const toLetterKey = (v) => {
      const n = Number(v);
      if (Number.isFinite(n) && String(v) !== "") return String.fromCharCode(65 + Math.max(0, Math.floor(n)));
      return String(v ?? "").trim().toUpperCase();
    };
    const getCaseCorrectKey = (sq = {}) => {
      const viaKey = String(sq.correctKey || "").trim().toUpperCase();
      if (viaKey) return viaKey;
      const opt = (sq.options || []).find((o) => o?.isCorrect);
      return opt?.key ? String(opt.key).toUpperCase() : "";
    };
    const readCaseChoice = (answers = {}, parentQ, subIndex, sq) => {
      const bySubId = answers[subIdFor(parentQ, sq, subIndex)];
      if (bySubId != null) return getAnsValue(bySubId);
      const parent = answers[parentQ?.id];
      if (parent && typeof parent === "object" && parent.value && typeof parent.value === "object") {
        return parent.value[subIndex];
      }
      return null;
    };
    const getCorrectShape = (q = {}) => {
      const qt = norm(q.questionType);
      if (qt === "mcq" || qt === "mcq_descriptive") {
        const opt = (q.options || []).find((o) => o?.isCorrect);
        return { type: "key", value: opt?.key ?? null };
      }
      if (qt === "multi_correct") {
        const keys = (q.options || []).filter((o) => o?.isCorrect).map((o) => String(o.key));
        return { type: "keyset", value: keys };
      }
      if (qt === "true_false" || qt === "tf") return { type: "scalar", value: String(q.trueFalseAnswer || "True") };
      if (qt === "match") {
        if (Number.isInteger(q.correctOptionIndex)) return { type: "index", value: Number(q.correctOptionIndex) };
        return { type: "unknown", value: null };
      }
      if (qt === "assertion_reason" || qt === "ar") return { type: "scalar", value: String(q.arAnswer || "A") };
      return { type: "unknown", value: null };
    };
    const isAnswerCorrect = (q, studentValue, correctShape) => {
      const qt = norm(q.questionType);
      const { type, value } = correctShape;
      if (type === "unknown") return null;

      const toKey = (v) => String(v ?? "").trim().toUpperCase();
      const toKeyArray = (v) =>
        Array.isArray(v)
          ? v.map((x) => toKey(x))
          : String(v ?? "")
              .split(",")
              .map((x) => toKey(x))
              .filter(Boolean);

      if (qt === "mcq" || qt === "mcq_descriptive") {
        if (type !== "key") return null;
        const sv = studentValue;
        const sKey =
          Number.isFinite(Number(sv)) && String(sv) !== ""
            ? String.fromCharCode(65 + Number(sv))
            : toKey(sv);
        return sKey && value ? sKey === toKey(value) : null;
      }
      if (qt === "multi_correct") {
        if (type !== "keyset") return null;
        const sKeys = new Set(toKeyArray(studentValue));
        const cKeys = new Set(value.map((x) => toKey(x)));
        if (sKeys.size !== cKeys.size) return false;
        for (const k of sKeys) if (!cKeys.has(k)) return false;
        return true;
      }
      if (qt === "true_false" || qt === "tf") {
        if (type !== "scalar") return null;
        return String(studentValue) === String(value);
      }
      if (qt === "match") {
        if (type !== "index") return null;
        return Number(studentValue) === Number(value);
      }
      if (qt === "assertion_reason" || qt === "ar") {
        const toLetter = (v) => {
          const n = Number(v);
          if (Number.isFinite(n)) return String.fromCharCode(65 + Math.max(0, Math.floor(n)));
          return String(v || "").toUpperCase();
        };
        const sKey = toLetter(studentValue);
        const cKey = toLetter(value);
        return sKey && cKey ? sKey === cKey : null;
      }
      return null;
    };

    if (!attempt || !questions?.length) {
      const score = Number(attempt?.score ?? attempt?.evaluatedScore ?? attempt?.marksObtained ?? attempt?.obtained ?? attempt?.points ?? 0) || 0;
      const total = Number(attempt?.totalMarks ?? attempt?.maxMarks ?? attempt?.fullMarks ?? attempt?.total ?? attempt?.outOf ?? 0) || 0;
      return {
        resultsByQuestion: {},
        answeredCount: Number(attempt?.answeredCount ?? 0),
        correctCount: Number(attempt?.correctCount ?? 0),
        score,
        total
      };
    }

    const answers = attempt.answers || {};
    const resultsByQuestion = {};
    let total = 0, score = 0, answered = 0, correct = 0;

    for (const q of questions) {
      const qt = norm(q.questionType);
      if (qt === "case_based" && Array.isArray(q.caseSubQs) && q.caseSubQs.length) {
        for (const [idx, sq] of q.caseSubQs.entries()) {
          const subId = subIdFor(q, sq, idx);
          const subMarks = Number(sq.marks ?? q.marks ?? 1) || 1;
          const subNeg = Number(sq.negativeMarks ?? q.negativeMarks ?? 0) || 0;
          total += subMarks;

          const raw = (() => {
            const bySubId = answers[subId];
            if (bySubId != null) return getAnsValue(bySubId);
            const parent = answers[q?.id];
            if (parent && typeof parent === "object" && parent.value && typeof parent.value === "object") return parent.value[idx];
            return null;
          })();

          const chosenKey = toLetterKey(getAnsValue(raw));
          const isAnswered = chosenKey !== "";
          if (isAnswered) answered += 1;

          const correctKey = getCaseCorrectKey(sq);
          const ok = chosenKey && correctKey ? chosenKey === correctKey : null;

          if (ok === true) {
            score += subMarks; correct += 1;
            resultsByQuestion[subId] = { correct: true, marks: subMarks, evaluated: true, parentId: q.id };
          } else if (ok === false) {
            score -= subNeg;
            resultsByQuestion[subId] = { correct: false, marks: -subNeg, evaluated: true, parentId: q.id };
          } else {
            resultsByQuestion[subId] = { correct: false, marks: 0, evaluated: false, parentId: q.id };
          }
        }
      } else {
        const marks = getMarks(q);
        const neg = getNegMarks(q);
        total += marks;

        const raw = answers[q.id];
        const ansVal = getAnsValue(raw);
        const isAnswered = Array.isArray(ansVal) ? ansVal.length > 0 : String(ansVal ?? "").trim() !== "";
        if (isAnswered) answered += 1;

        const correctShape = getCorrectShape(q);
        const ok = isAnswerCorrect(q, ansVal, correctShape);

        if (ok === null) {
          resultsByQuestion[q.id] = { correct: false, marks: 0, evaluated: false };
        } else if (ok) {
          score += marks; correct += 1;
          resultsByQuestion[q.id] = { correct: true, marks, evaluated: true };
        } else {
          score -= neg;
          resultsByQuestion[q.id] = { correct: false, marks: -neg, evaluated: true };
        }
      }
    }

    return { resultsByQuestion, answeredCount: answered, correctCount: correct, score, total };
  }, []);

  /* ===== load data ===== */
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true); setErr("");
        if (!schoolId || !examId || !uid) {
          setErr("Missing context.");
          setLoading(false);
          return;
        }
        const exRef = doc(db, "schools", schoolId, "exams", examId);
        const exSnap = await getDoc(exRef);
        if (!exSnap.exists()) throw new Error("Exam not found.");
        const ex = { id: exSnap.id, ...exSnap.data() };
        if (!mounted) return;
        setExam(ex);

        let qs = [];
        if (ex.templateId) {
          const qCol = collection(db, "schools", schoolId, "questions");
          const qy = query(qCol, where("templateId", "==", ex.templateId), qLimit(800));
          const qSnap = await getDocs(qy);
          qs = qSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        }
        if (!mounted) return;
        setQuestions(qs);

        const attRef = doc(db, "schools", schoolId, "exams", examId, "attempts", uid);
        const attSnap = await getDoc(attRef);
        const rawAttempt = attSnap.exists() ? { id: attSnap.id, ...attSnap.data() } : null;

        if (rawAttempt) {
          const evald = evaluateAttemptLocally(rawAttempt, qs);
          const merged = {
            ...rawAttempt,
            resultsByQuestion: evald.resultsByQuestion,
            answeredCount: evald.answeredCount,
            correctCount: evald.correctCount,
            score: evald.score,
            totalMarks: evald.total,
            evaluatedScore: evald.score,
          };
          if (!mounted) return;
          setAttempt(merged);
        } else {
          if (!mounted) return;
          setAttempt(null);
        }
      } catch (e) {
        if (!mounted) return;
        console.error(e);
        setErr(e?.message || "Failed to load analysis.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [schoolId, examId, uid, evaluateAttemptLocally]);

  /* ===== derived ===== */
  const scoreInfo = useMemo(() => {
    if (!attempt) return { score: 0, total: 0, percent: 0, answered: 0, correct: 0, accuracy: 0, timeSec: 0 };
    const score = Number.isFinite(attempt.score) ? Number(attempt.score) : Number(attempt.evaluatedScore || 0);
    let total = Number(attempt.totalMarks || readTotal(attempt));
    if (!total && questions.length) {
      total = questions.reduce((sum, q) => {
        const qt = String(q.questionType || "").toLowerCase();
        if (qt === "case_based" && Array.isArray(q.caseSubQs) && q.caseSubQs.length) {
          return sum + q.caseSubQs.reduce((s, sq) => s + (Number(sq.marks ?? q.marks ?? 1) || 1), 0);
        }
        return sum + getMarks(q);
      }, 0);
    }
    const answered = Number(attempt.answeredCount ?? 0);
    const correct = Number(attempt.correctCount ?? 0);
    const percent = total ? Math.max(0, Math.min(100, (score / total) * 100)) : 0;
    const accuracy = answered ? Math.max(0, Math.min(100, (correct / answered) * 100)) : 0;

    const start = toJS(attempt.startedAt);
    const end = toJS(attempt.submittedAt);
    const timeSec = start && end ? Math.max(0, Math.floor((end.getTime() - start.getTime()) / 1000)) : 0;

    return { score, total, percent, answered, correct, accuracy, timeSec };
  }, [attempt, questions]);

  const timeLabel = useMemo(() => {
    const s = scoreInfo.timeSec || 0;
    const d = dayjs.duration(s, "seconds");
    const hh = String(Math.floor(d.asHours())).padStart(2, "0");
    const mm = String(d.minutes()).padStart(2, "0");
    const ss = String(d.seconds()).padStart(2, "0");
    return `${hh}:${mm}:${ss}`;
  }, [scoreInfo.timeSec]);

  const qItems = useMemo(() => {
    if (!attempt || !questions.length) return [];
    const rbq = attempt.resultsByQuestion && typeof attempt.resultsByQuestion === "object" ? attempt.resultsByQuestion : {};
    const answers = attempt.answers || {};
    const items = [];
    const typeLabel = (qt) => {
      const t = nrm(qt);
      if (t === "mcq") return "MCQ";
      if (t === "multi_correct") return "Multi Correct";
      if (t === "true_false") return "True/False";
      if (t === "match") return "Match";
      if (t === "assertion_reason" || t === "ar") return "A/R";
      if (t === "case_based") return "Case";
      if (t === "mcq_descriptive") return "MCQ+Desc";
      if (t === "descriptive") return "Descriptive";
      if (t === "statement_based") return "Statement";
      return (qt || "").toUpperCase();
    };
    const readCaseChoice = (answers = {}, parentQ, subIndex, sq) => {
      const bySubId = answers[subIdFor(parentQ, sq, subIndex)];
      if (bySubId != null) return getAnsValue(bySubId);
      const parent = answers[parentQ?.id];
      if (parent && typeof parent === "object" && parent.value && typeof parent.value === "object") {
        return parent.value[subIndex];
      }
      return null;
    };
    const toKey = (v) => (Number.isFinite(Number(v)) && String(v) !== "" ? String.fromCharCode(65 + Number(v)) : String(v ?? "").toUpperCase());
    const parentExpMap = new Map(questions.map((q) => [q.id, q.explanationHtml || ""]));

    questions.forEach((q) => {
      const qt = nrm(q.questionType);

      if (qt === "case_based" && Array.isArray(q.caseSubQs) && q.caseSubQs.length) {
        q.caseSubQs.forEach((sq, idx) => {
          const qid = subIdFor(q, sq, idx);
          const evalInfo = rbq[qid] || {};
          const studentRaw = readCaseChoice(answers, q, idx, sq);
          const studentVal = getAnsValue(studentRaw);
          const chosenKey = toKey(studentVal);
          const correctKey = getCaseCorrectKey(sq);
          const awarded = Number(evalInfo?.marks ?? 0) || 0;
          const marks = Number(sq.marks ?? q.marks ?? 1) || 1;
          const correctness = evalInfo.evaluated ? (evalInfo.correct ? "correct" : "wrong") : chosenKey ? "wrong" : "unattempted";

          items.push({
            id: qid,
            parentId: q.id,
            indexLabel: `Case ${idx + 1}`,
            subject: q.subject || "General",
            topic: q.topic || "—",
            typeLabel: "Case",
            questionHtml: sq.questionHtml || "",
            passageHtml: q.casePassageHtml || q.passageHtml || "",
            explanationHtml: parentExpMap.get(q.id) || "",
            options: sq.options || [],
            chosen: chosenKey || "",
            correct: correctKey || "",
            awarded,
            marks,
            correctness,
            sectionKey: q.sectionKey || "",
          });
        });
        return;
      }

      const qid = q.id;
      const evalInfo = rbq[qid] || {};
      const studentRaw = answers[qid];
      const studentVal = getAnsValue(studentRaw);
      const answered = String(studentVal ?? "").trim() !== "";
      const correctness = evalInfo.evaluated ? (evalInfo.correct ? "correct" : "wrong") : answered ? "wrong" : "unattempted";

      let chosenLabel = "", correctLabel = "";
      const tLabel = typeLabel(q.questionType);
      const stemHtml = qt === "assertion_reason" || qt === "ar" ? buildARStemHtml(q) : (q.questionHtml || "");

      if (qt === "mcq" || qt === "mcq_descriptive") {
        const chosen = toKey(studentVal);
        const correct = (q.options || []).find((o) => o.isCorrect)?.key ?? "";
        chosenLabel = chosen; correctLabel = correct;
      } else if (qt === "multi_correct") {
        const chosen = Array.isArray(studentVal)
          ? studentVal
          : String(studentVal || "").split(",").map((s) => s.trim()).filter(Boolean);
        const correct = (q.options || []).filter((o) => o.isCorrect).map((o) => o.key);
        chosenLabel = chosen.map((x) => String(x).toUpperCase()).join(", ");
        correctLabel = correct.join(", ");
      } else if (qt === "true_false") {
        chosenLabel = String(studentVal || ""); correctLabel = String(q.trueFalseAnswer || "True");
      } else if (qt === "match") {
        chosenLabel = Number.isInteger(studentVal) && studentVal >= 0 && studentVal <= 3 ? `Option ${Number(studentVal) + 1}` : "";
        correctLabel = Number.isInteger(q.correctOptionIndex) && q.correctOptionIndex >= 0 ? `Option ${q.correctOptionIndex + 1}` : "";
      } else if (qt === "assertion_reason" || qt === "ar") {
        const toLetter = (v) => {
          const n = Number(v);
          if (Number.isFinite(n)) return String.fromCharCode(65 + Math.max(0, Math.floor(n)));
          return String(v || "").toUpperCase();
        };
        chosenLabel = toLetter(studentVal); correctLabel = toLetter(q.arAnswer || "A");
      } else {
        chosenLabel = String(studentVal ?? ""); correctLabel = "—";
      }

      items.push({
        id: qid,
        parentId: null,
        indexLabel: "",
        subject: q.subject || "General",
        topic: q.topic || "—",
        typeLabel: tLabel,
        questionHtml: stemHtml,
        passageHtml: q.passageHtml || "",
        explanationHtml: q.explanationHtml || "",
        options: q.options || [],
        chosen: chosenLabel,
        correct: correctLabel,
        awarded: Number(evalInfo?.marks ?? 0) || 0,
        marks: getMarks(q),
        correctness,
        sectionKey: q.sectionKey || "",
      });
    });

    return items;
  }, [attempt, questions]);

  /* ===== filter options ===== */
  const subjectOptions = useMemo(
    () => ["all", ...Array.from(new Set(qItems.map((i) => i.subject))).sort()],
    [qItems]
  );
  const topicOptions = useMemo(() => {
    const list = Array.from(new Set(qItems.map((i) => i.topic).filter(Boolean)));
    return ["all", ...list.sort()];
  }, [qItems]);
  const typeOptions = useMemo(() => {
    const list = Array.from(new Set(qItems.map((i) => i.typeLabel)));
    return ["all", ...list.sort()];
  }, [qItems]);

  /* ===== filtered list ===== */
  const filteredQItems = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return qItems.filter((i) => {
      if (fSubject !== "all" && i.subject !== fSubject) return false;
      if (fTopic !== "all" && i.topic !== fTopic) return false;
      if (fType !== "all" && i.typeLabel !== fType) return false;
      if (fCorrect !== "all" && i.correctness !== fCorrect) return false;

      if (needle) {
        const plainQ = (i.questionHtml || "").replace(/<[^>]*>/g, " ").toLowerCase();
        const plainPass = (i.passageHtml || "").replace(/<[^>]*>/g, " ").toLowerCase();
        const hay = [
          i.subject, i.topic, i.typeLabel, i.correct, i.chosen, plainQ, plainPass,
        ].map((s) => String(s || "").toLowerCase()).join(" ");
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [qItems, fSubject, fTopic, fType, fCorrect, q]);

  /* ===== expand/collapse state ===== */
  const [expanded, setExpanded] = useState(() => new Set());
  const toggleRow = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="py-4 text-center">
        <Spinner animation="border" size="sm" /> Loading analysis…
      </div>
    );
  }
  if (err) return <Alert variant="danger" className="mb-0">{err}</Alert>;
  if (!attempt || !exam) return <Alert variant="warning" className="mb-0">No analysis available.</Alert>;

  const answeredCount = attempt.answeredCount ?? 0;
  const wrongCount = Math.max(0, answeredCount - (attempt.correctCount ?? 0));
  const totalQuestions = questions.length;
  const unattemptedCount = Math.max(0, totalQuestions - answeredCount);

  return (
    <div>
      {/* Summary header (compact for modal) */}
      <Card className="border-0 shadow-sm mb-3">
        <Card.Body className="py-3">
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
            <div className="min-w-0">
              <div className="fw-bold text-truncate">{exam.name || "Exam"}</div>
              <div className="small text-muted">
                Score:&nbsp;<b>{Math.round(scoreInfo.score * 100) / 100}</b> / {scoreInfo.total || "—"} &nbsp;•&nbsp;
                Percent:&nbsp;<b>{Math.round(scoreInfo.percent)}%</b> &nbsp;•&nbsp;
                Accuracy:&nbsp;<b>{Math.round(scoreInfo.accuracy)}%</b> &nbsp;•&nbsp;
                Time:&nbsp;<b>{timeLabel}</b>
              </div>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <Badge bg={scoreInfo.percent >= 75 ? "success" : scoreInfo.percent >= 50 ? "info" : scoreInfo.percent >= 33 ? "warning" : "danger"}>
                {Math.round(scoreInfo.percent)}%
              </Badge>
              <Badge bg="info">{unattemptedCount} Unattempted</Badge>
              <Badge bg="primary">{answeredCount} Attempted</Badge>
              <Badge bg="success">{attempt.correctCount ?? 0} Correct</Badge>
              <Badge bg="danger">{wrongCount} Wrong</Badge>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Filters */}
      <Card className="border-0 shadow-sm mb-3">
        <Card.Body className="py-3">
          <Row className="g-2 align-items-center">
            <Col xs={12} md={4}>
              <InputGroup>
                <InputGroup.Text className="bg-light">
                  <i className="bi bi-search" />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search question / subject / topic / your answer"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col xs={6} md={2}>
              <Form.Select value={fSubject} onChange={(e) => setFSubject(e.target.value)}>
                {subjectOptions.map((s) => (
                  <option key={s} value={s}>{s === "all" ? "All subjects" : s}</option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={6} md={2}>
              <Form.Select value={fTopic} onChange={(e) => setFTopic(e.target.value)}>
                {topicOptions.map((t) => (
                  <option key={t} value={t}>{t === "all" ? "All topics" : t}</option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={6} md={2}>
              <Form.Select value={fType} onChange={(e) => setFType(e.target.value)}>
                {typeOptions.map((t) => (
                  <option key={t} value={t}>{t === "all" ? "All types" : t}</option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={6} md={2}>
              <Form.Select value={fCorrect} onChange={(e) => setFCorrect(e.target.value)}>
                <option value="all">All</option>
                <option value="correct">Correct</option>
                <option value="wrong">Wrong</option>
                <option value="unattempted">Unattempted</option>
              </Form.Select>
            </Col>
          </Row>

          <div className="mt-2 d-flex gap-2 flex-wrap align-items-center">
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => {
                setQ("");
                setFSubject("all");
                setFTopic("all");
                setFType("all");
                setFCorrect("all");
              }}
            >
              Reset
            </Button>
            <div className="small text-muted">
              Showing <b>{filteredQItems.length}</b> of {qItems.length}
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Question-wise table */}
      <Card className="border-0 shadow-sm">
        <Card.Header className="fw-semibold d-flex justify-content-between align-items-center">
          <div>Question-wise Analysis</div>
          <div className="small text-muted">Tap a row to expand details</div>
        </Card.Header>
        <Card.Body className="p-0">
          {filteredQItems.length === 0 ? (
            <div className="p-3 text-muted">Nothing to show. Try changing filters.</div>
          ) : (
            <Table hover responsive className="mb-0 align-middle">
              <thead>
                <tr>
                  <th style={{ width: 52 }} className="text-center">#</th>
                  <th>Subject / Topic</th>
                  <th className="d-none d-sm-table-cell">Type</th>
                  <th className="text-center d-none d-md-table-cell">Your Answer</th>
                  <th className="text-center d-none d-md-table-cell">Correct</th>
                  <th className="text-center">Marks</th>
                  <th className="text-center">Result</th>
                </tr>
              </thead>
              <tbody>
                {filteredQItems.map((it, idx) => {
                  const isOpen = expanded.has(it.id);
                  return (
                    <React.Fragment key={it.id}>
                      <tr
                        style={{ cursor: "pointer" }}
                        onClick={() => toggleRow(it.id)}
                        className={isOpen ? "table-active" : undefined}
                      >
                        <td className="text-center">{it.indexLabel || idx + 1}</td>
                        <td>
                          <div className="fw-semibold">{it.subject}</div>
                          <div className="text-muted small text-truncate" title={`${it.topic}${it.sectionKey ? ` • Sec ${it.sectionKey}` : ""}`}>
                            {it.topic} {it.sectionKey ? `• Sec ${it.sectionKey}` : ""}
                          </div>
                          <div className="d-md-none mt-1 small">
                            <span className="me-2">You: {it.chosen || "—"}</span>
                            <span className="text-muted">Correct: {it.correct || "—"}</span>
                          </div>
                        </td>
                        <td className="d-none d-sm-table-cell">
                          <Badge bg="light" text="dark">{it.typeLabel}</Badge>
                        </td>
                        <td className="text-center d-none d-md-table-cell">
                          {it.chosen ? <Badge bg="light" text="dark">{it.chosen}</Badge> : <span className="text-muted">—</span>}
                        </td>
                        <td className="text-center d-none d-md-table-cell">
                          {it.correct ? <Badge bg="info" text="dark">{it.correct}</Badge> : <span className="text-muted">—</span>}
                        </td>
                        <td className="text-center">
                          <span className={it.awarded > 0 ? "text-success" : it.awarded < 0 ? "text-danger" : "text-muted"}>
                            {it.awarded > 0 ? `+${it.awarded}` : it.awarded}
                          </span>{" "}
                          <span className="text-muted">/ {it.marks}</span>
                        </td>
                        <td className="text-center">
                          {it.correctness === "correct" && <Badge bg="success">Correct</Badge>}
                          {it.correctness === "wrong" && <Badge bg="danger">Wrong</Badge>}
                          {it.correctness === "unattempted" && <Badge bg="secondary">Unattempted</Badge>}
                        </td>
                      </tr>

                      {isOpen && (
                        <tr>
                          <td colSpan={7} className="p-3" style={{ background: "rgba(0,0,0,.02)" }}>
                            {it.passageHtml ? (
                              <div className="mb-3">
                                <div className="text-muted small">Passage</div>
                                <div dangerouslySetInnerHTML={{ __html: it.passageHtml }} />
                              </div>
                            ) : null}

                            <div className="mb-2">
                              <div className="text-muted small">Question</div>
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: it.questionHtml || "<em class='text-muted'>No content</em>",
                                }}
                              />
                            </div>

                            {it.typeLabel === "A/R" && (
                              <div className="mt-2 small text-muted">
                                <ol className="mb-0">
                                  <li>A — Both true, Reason explains Assertion</li>
                                  <li>B — Both true, Reason does not explain Assertion</li>
                                  <li>C — Assertion true, Reason false</li>
                                  <li>D — Assertion false</li>
                                </ol>
                              </div>
                            )}

                            {(it.options || []).length > 0 && (
                              <Row className="g-2 mb-2 mt-2">
                                {it.options.map((o) => {
                                  const chosenSet = new Set((it.chosen || "").split(",").map((s) => s.trim()).filter(Boolean));
                                  const correctSet = new Set((it.correct || "").split(",").map((s) => s.trim()).filter(Boolean));
                                  const isChosen = chosenSet.has(o.key);
                                  const isCorrect = correctSet.has(o.key);
                                  const variant = isCorrect ? "success" : isChosen ? "danger" : "light";
                                  return (
                                    <Col md={6} key={o.key}>
                                      <Card className="h-100" border={isCorrect ? "success" : isChosen ? "danger" : "light"}>
                                        <Card.Body className="py-2">
                                          <div className="d-flex align-items-start gap-2">
                                            <div style={{ width: 28 }}>
                                              <Badge bg={variant} text={variant === "light" ? "dark" : undefined}>
                                                {o.key}
                                              </Badge>
                                            </div>
                                            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                              <div
                                                dangerouslySetInnerHTML={{
                                                  __html: o.value || "<em class='text-muted'>—</em>",
                                                }}
                                              />
                                            </div>
                                          </div>
                                        </Card.Body>
                                      </Card>
                                    </Col>
                                  );
                                })}
                              </Row>
                            )}

                            {it.explanationHtml ? (
                              <>
                                <hr />
                                <div className="text-muted small">Explanation</div>
                                <div
                                  className="mt-1"
                                  dangerouslySetInnerHTML={{
                                    __html: it.explanationHtml || "<em class='text-muted'>No explanation provided.</em>",
                                  }}
                                />
                              </>
                            ) : (
                              <div className="text-muted small"><em>No explanation provided.</em></div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <style>{`
        .table-hover tbody tr:hover { transition: background-color .2s ease; }
        @media (max-width: 576px) {
          .table thead th:nth-child(3),
          .table thead th:nth-child(4),
          .table thead th:nth-child(5) { display: none !important; }
        }
      `}</style>
    </div>
  );
}
function EvaluateDescriptiveModal({ show, onHide, schoolId, row, teacher, onNext = null }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exam, setExam] = useState(null);
  const [obtained, setObtained] = useState("");
  const [total, setTotal] = useState(0);
  const [remarks, setRemarks] = useState("");
  const [err, setErr] = useState("");

  const [finalizeNow, setFinalizeNow] = useState(true);
  const [notifyStudent, setNotifyStudent] = useState(true);

  const qtype = (q) => String(q?.questionType || "").toLowerCase();
  const computeTotalFromQuestions = (qs = []) =>
    qs.reduce((sum, q) => {
      if (qtype(q) === "case_based" && Array.isArray(q.caseSubQs) && q.caseSubQs.length) {
        return sum + q.caseSubQs.reduce((s, sq) => s + (Number(sq.marks ?? q.marks ?? 1) || 1), 0);
      }
      return sum + (Number(q.marks ?? 1) || 1);
    }, 0);

  useEffect(() => {
    if (!show || !row || !schoolId) return;
    let alive = true;
    (async () => {
      try {
        setLoading(true); setErr("");
        const exRef = doc(db, "schools", schoolId, "exams", row._examId);
        const exSnap = await getDoc(exRef);
        const ex = exSnap.exists() ? ({ id: exSnap.id, ...exSnap.data() }) : null;
        if (!alive) return;
        setExam(ex);

        const got = row.obtained ?? row.evaluatedScore ?? row.score ?? row.marksObtained ?? null;
        setObtained(got != null ? String(got) : "");
        setRemarks(row.remarks || "");

        let tot = Number(row.total ?? row.totalMarks ?? ex?.totalMarks ?? ex?.maxMarks ?? ex?.fullMarks ?? ex?.total ?? 0) || 0;

        if (!tot) {
          let qs = [];
          if (ex?.templateId) {
            const qc = collection(db, "schools", schoolId, "questions");
            const qy = query(qc, where("templateId", "==", ex.templateId), qLimit(1500));
            const qsnap = await getDocs(qy);
            qs = qsnap.docs.map(d => ({ id: d.id, ...(d.data() || {}) }));
          } else {
            const qc = collection(db, "schools", schoolId, "exams", row._examId, "questions");
            const qsnap = await getDocs(qc);
            qs = qsnap.docs.map(d => ({ id: d.id, ...(d.data() || {}) }));
          }
          tot = computeTotalFromQuestions(qs);
        }
        if (!alive) return;
        setTotal(tot || 0);
      } catch (e) {
        if (!alive) return;
        setErr(e?.message || "Failed to load exam/total.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [show, row, schoolId]);

  const onSave = async (opts = {}) => {
    setErr("");
    const oRaw = Number(obtained);
    const tRaw = Number(total);
    if (!Number.isFinite(oRaw) || !Number.isFinite(tRaw)) return setErr("Please enter numeric marks.");
    if (tRaw <= 0) return setErr("Total must be greater than 0.");
    if (oRaw < 0 || oRaw > tRaw) return setErr("Obtained must be between 0 and Total.");

    try {
      setSaving(true);

      const attemptDocId = row?.uid || row?.id || row?.byStudent?.uid;
      const _schoolId = schoolId || row?.schoolId || exam?.schoolId;
      const _examId   = row?._examId || row?.examId || exam?.id;

      if (!_schoolId || !_examId || !attemptDocId) {
        throw new Error("Missing identifiers to save evaluation (school/exam/attempt).");
      }

      const ref = doc(db, "schools", _schoolId, "exams", _examId, "attempts", attemptDocId);
      await setDoc(ref, {
        status: finalizeNow ? "evaluated" : "draft",
        evaluatedScore: oRaw,
        totalMarks: tRaw,
        remarks: String(remarks || ""),
        evaluatedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        notifyStudent: !!notifyStudent,
        evaluatedBy: {
          id: teacher?.id || "",
          name: teacher?.name || "",
          email: teacher?.email || ""
        }
      }, { merge: true });

      toast?.success(finalizeNow ? "Marks saved & finalized." : "Draft saved");
      onHide?.();
      if (opts.goNext && typeof onNext === "function") onNext();
    } catch (e) {
      setErr(e?.message || "Could not save marks.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Evaluate Descriptive Attempt</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div className="py-3 text-center"><Spinner animation="border" size="sm" /></div>
        ) : (
          <>
            {err && <Alert variant="danger" className="py-2">{err}</Alert>}

            <div className="mb-2">
              <div className="fw-semibold">
                {row?.studentName || row?.name || row?.byStudent?.name || "Student"}
              </div>
              <div className="small text-muted">
                {exam?.name || "Exam"} {row?.upload ? "• " : ""}
                {row?.upload && (
                  <Button
                    size="sm"
                    variant="link"
                    className="p-0 align-baseline"
                    onClick={() => window.open(row.upload.url || "", "_blank", "noopener")}
                  >
                    Open Answer PDF
                  </Button>
                )}
                {exam?.modelAnswerPdfUrl && (
                  <>
                    {" • "}
                    <Button
                      size="sm"
                      variant="link"
                      className="p-0 align-baseline"
                      onClick={() => window.open(exam.modelAnswerPdfUrl, "_blank", "noopener")}
                    >
                      Model Answer
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* numbers + live preview */}
            {(() => {
              const o = Number(obtained || 0);
              const t = Number(total || 0);
              const pct = t > 0 ? Math.round((o / t) * 1000) / 10 : null;
              const grade =
                pct == null ? null :
                pct >= 90 ? "A+" :
                pct >= 80 ? "A"  :
                pct >= 70 ? "B"  :
                pct >= 60 ? "C"  :
                pct >= 50 ? "D"  : "F";
              const invalid = o < 0 || t <= 0 || o > t;

              return (
                <>
                  <Row className="g-2">
                    <Col sm={6}>
                      <Form.Group>
                        <Form.Label>Obtained Marks</Form.Label>
                        <Form.Control
                          type="number"
                          inputMode="decimal"
                          min="0"
                          step="0.5"
                          value={obtained}
                          onChange={(e) => setObtained(e.target.value)}
                          onBlur={() => {
                            const n = Math.max(0, Number(obtained || 0));
                            setObtained(String(Math.min(n, Number(total || Infinity))));
                          }}
                          placeholder="e.g. 37.5"
                        />
                      </Form.Group>
                    </Col>
                    <Col sm={6}>
                      <Form.Group>
                        <Form.Label>Total</Form.Label>
                        <Form.Control
                          type="number"
                          inputMode="decimal"
                          min="0"
                          step="0.5"
                          value={total}
                          onChange={(e) => setTotal(e.target.value)}
                          onBlur={() => setTotal(String(Math.max(0, Number(total || 0))))}
                          placeholder="e.g. 80"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <div className="d-flex align-items-center gap-3 mt-2">
                    <Badge bg={pct == null ? "secondary" : "dark"}>
                      {pct == null ? "—%" : `${pct}%`}
                    </Badge>
                    {grade && <Badge bg={grade === "F" ? "danger" : "success"}>Grade {grade}</Badge>}
                    {invalid && (
                      <span className="text-danger small">Obtained cannot exceed Total and values must be ≥ 0.</span>
                    )}
                  </div>
                </>
              );
            })()}

            {/* quick remarks */}
            <Form.Group className="mt-3">
              <Form.Label>Remarks (optional)</Form.Label>
              <div className="d-flex flex-wrap gap-2 mb-2">
                {["Excellent work", "Good attempt", "Need to improve", "Incomplete answers", "Please write steps clearly"].map((r) => (
                  <Button
                    key={r}
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setRemarks(prev => prev ? `${prev} ${r}.` : r)}
                  >
                    {r}
                  </Button>
                ))}
              </div>
              <Form.Control
                as="textarea"
                rows={2}
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                maxLength={500}
                placeholder="Add a note for the student (optional)"
              />
              <div className="small text-muted mt-1">{remarks?.length || 0}/500</div>
            </Form.Group>

            {/* workflow opts */}
            <Row className="g-2 mt-2">
              <Col sm="auto">
                <Form.Check
                  type="switch"
                  id="finalize-switch"
                  label="Finalize now"
                  checked={finalizeNow}
                  onChange={(e) => setFinalizeNow(e.target.checked)}
                />
              </Col>
              <Col sm="auto">
                <Form.Check
                  type="switch"
                  id="notify-switch"
                  label="Notify student"
                  checked={notifyStudent}
                  onChange={(e) => setNotifyStudent(e.target.checked)}
                />
              </Col>
            </Row>

            <div className="small text-muted mt-2">
              Saving will mark this attempt as <b>{finalizeNow ? "evaluated" : "draft"}</b>.
              {notifyStudent ? " The student will be notified." : ""}
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={saving}>Close</Button>
        {onNext && (
          <Button
            variant="outline-primary"
            disabled={saving || loading}
            onClick={() => onSave({ goNext: true })}
          >
            {saving ? "Saving…" : "Save & Next"}
          </Button>
        )}
        <Button
          onClick={() => onSave({ goNext: false })}
          className="btn-accent"
          disabled={saving || loading}
        >
          {saving ? "Saving…" : "Save Marks"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
export default function TeacherStudentResultsDashboard() {
  const { user, profile } = useAuth();
  const schoolId = profile?.schoolId || localStorage.getItem("schoolId") || null;

  /* -------- teacher -------- */
  const [teacher, setTeacher] = useState(null);
  const [loadingTeacher, setLoadingTeacher] = useState(true);
  const [teacherErr, setTeacherErr] = useState("");
  const aliveRef = useRef(true);
  useEffect(() => { aliveRef.current = true; return () => { aliveRef.current = false; }; }, []);

  useEffect(() => {
    if (!schoolId || !user?.uid) { setTeacher(null); setLoadingTeacher(false); return; }
    setLoadingTeacher(true);
    setTeacherErr("");
    (async () => {
      try {
        if (profile?.teacherId) {
          const ref = doc(db, "schools", schoolId, "teachers", profile.teacherId);
          const snap = await getDoc(ref);
          if (!aliveRef.current) return;
          if (snap.exists()) { setTeacher({ id: snap.id, ...(snap.data() || {}) }); setLoadingTeacher(false); return; }
        }
        const email = lower(user?.email || "");
        if (email) {
          const qs = await getDocs(query(collection(db, "schools", schoolId, "teachers"), where("email", "==", email), qLimit(1)));
          if (!aliveRef.current) return;
          if (!qs.empty) { const d = qs.docs[0]; setTeacher({ id: d.id, ...(d.data() || {}) }); setLoadingTeacher(false); return; }
        }
        if (aliveRef.current) { setTeacher(null); setTeacherErr("Teacher record not found for your account."); setLoadingTeacher(false); }
      } catch {
        if (!aliveRef.current) return;
        setTeacher(null); setTeacherErr("Failed to load teacher profile."); setLoadingTeacher(false);
      }
    })();
  }, [schoolId, user?.uid, user?.email, profile?.teacherId]);

  const isInactive = String(teacher?.status || "").toLowerCase() === "inactive";
  const teacherName = teacher?.name || profile?.displayName || user?.displayName || user?.email || "Teacher";

  // teacher classes & sections
  const teacherClasses = useMemo(
    () =>
      Array.isArray(teacher?.classIds) && teacher.classIds.length
        ? teacher.classIds.filter(Boolean)
        : (teacher?.className ? [teacher.className] : []),
    [teacher]
  );
  const teacherSectionsU = useMemo(
    () => parseSections(teacher?.sectionIds ?? teacher?.sections ?? teacher?.section),
    [teacher]
  );

  // active class
  const [activeClass, setActiveClass] = useState("");
  useEffect(() => {
    if (!teacherClasses.length) { setActiveClass(""); return; }
    setActiveClass(prev => (teacherClasses.includes(prev) ? prev : teacherClasses[0]));
  }, [teacherClasses]);

  /* -------- exams (list + filter) -------- */
  const [exams, setExams] = useState([]);
  const [loadingExams, setLoadingExams] = useState(true);
  const [testType, setTestType] = useState("");
  const [examId, setExamId] = useState("");
  const [examMode, setExamMode] = useState(""); // "" = All modes
const getExamMode = (ex) =>
  String(
    ex?.mode ?? ex?.examMode ?? (ex?.isMock ? "mock" : ex?.isLive ? "live" : "")
  )
    .trim()
    .toLowerCase();
  useEffect(() => {
    if (!schoolId || !activeClass) { setExams([]); setLoadingExams(false); return; }
    setLoadingExams(true);
    const ref = collection(db, "schools", schoolId, "exams");

    const unsub = onSnapshot(
      query(ref, where("classIds", "array-contains", activeClass), qLimit(500)),
      (snap) => {
        const all = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) =>
            (toDate(b.createdAt) || toDate(b.date) || 0) -
            (toDate(a.createdAt) || toDate(a.date) || 0)
          );

        const relevant = all.filter(ex => examTargetsTeacher(ex, activeClass, teacherSectionsU));
        setExams(relevant);
        setLoadingExams(false);

        if (examId && !relevant.some(x => x.id === examId)) {
          setExamId(relevant[0]?.id || "");
        }
      },
      () => setLoadingExams(false)
    );
    return () => unsub();
  }, [schoolId, activeClass, teacherSectionsU, examId]);

  const testTypeOptions = useMemo(() => {
    const s = new Set(); exams.forEach(ex => { const t = getTestType(ex); if (t) s.add(t); });
    return Array.from(s).sort((a,b)=>a.localeCompare(b));
  }, [exams]);

const filteredExams = useMemo(() => {
  let list = exams;
  if (testType) list = list.filter(ex => getTestType(ex) === testType);
  if (examMode) list = list.filter(ex => getExamMode(ex) === examMode);
  return list;
}, [exams, testType, examMode]);;

  useEffect(() => {
    if (examId === "") return;
    if (!filteredExams.some(e => e.id === examId)) setExamId(filteredExams[0]?.id || "");
  }, [filteredExams, examId]);

  /* -------- students (class/sections) -------- */
  const [students, setStudents] = useState([]);
  const [studentById, setStudentById] = useState({});
  const [studentByUid, setStudentByUid] = useState({});
  const [loadingStudents, setLoadingStudents] = useState(false);

  useEffect(() => {
    const className = activeClass || "";
    if (!schoolId || !className) {
      setStudents([]); setStudentById({}); setStudentByUid({}); setLoadingStudents(false); return;
    }
    setLoadingStudents(true);
    let isStale = false;

    const col = collection(db, "schools", schoolId, "students");
    const unsub = onSnapshot(
      query(col, where("className", "==", className)),
      (snap) => {
        if (isStale) return;
        const list = snap.docs
          .map((d) => {
            const s = d.data() || {};
            return {
              id: d.id,
              uid: s?.auth?.uid || s?.uid || "",
              name: s.name || s.displayName || "—",
              rollNo: s.rollNo || s.studentId || "",
              photoURL: s.photoURL || "",
              className: s.className || "",
              section: s.section || "",
              branch: s.branch || s.stream || "",
              status: s.status || "active",
              lastAttendanceStatus: s.lastAttendanceStatus || "",
              lastAttendanceDate: toDate(s.lastAttendanceDate),
            };
          })
          .filter((s) => String(s.className) === String(activeClass))
          .filter((s) => {
            if (teacherSectionsU.length === 0) return true;
            const secU = U(s.section || "");
            return secU === "" || teacherSectionsU.includes(secU);
          })
          .filter((s) => norm(s.status) !== "inactive")
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

        const byId  = {};
        const byUid = {};
        for (const st of list) {
          byId[st.id] = st;
          if (st.uid) byUid[st.uid] = st;
        }
        setStudents(list);
        setStudentById(byId);
        setStudentByUid(byUid);
        setLoadingStudents(false);
      },
      () => setLoadingStudents(false)
    );

    return () => { isStale = true; try { unsub(); } catch {} };
  }, [schoolId, activeClass, teacherSectionsU]);

  /* -------- questions / totals (for selected exam) -------- */
  const [questions, setQuestions] = useState([]);
  const [subjectTotals, setSubjectTotals] = useState({});
  const [examTotal, setExamTotal] = useState(0);

  useEffect(() => {
    if (!schoolId || !examId) { setQuestions([]); setSubjectTotals({}); setExamTotal(0); return; }
    (async () => {
      try {
        const exRef = doc(db, "schools", schoolId, "exams", examId);
        const exSnap = await getDoc(exRef);
        const ex = exSnap.exists() ? { id: exSnap.id, ...exSnap.data() } : null;

        let total = Number(ex?.maxMarks || 0) || 0;
        let qs = [];

        if (ex?.templateId) {
          const qCol = collection(db, "schools", schoolId, "questions");
          const qy = query(qCol, where("templateId", "==", ex.templateId), qLimit(1200));
          const qSnap = await getDocs(qy);
          qs = qSnap.docs.map(d => ({ id: d.id, ...d.data() }));

          if (!total) {
            total = qs.reduce((sum, q) => {
              const qt = String(q.questionType || "").toLowerCase();
              if (qt === "case_based" && Array.isArray(q.caseSubQs) && q.caseSubQs.length) {
                return sum + q.caseSubQs.reduce((s, sq) => s + (Number(sq.marks ?? q.marks ?? 1) || 1), 0);
              }
              return sum + (Number(q.marks ?? 1) || 1);
            }, 0);
          }
        }

        const subjTotals = {};
        qs.forEach(q => {
          const subject = q.subject || "General";
          const qt = String(q.questionType || "").toLowerCase();
          if (qt === "case_based" && Array.isArray(q.caseSubQs) && q.caseSubQs.length) {
            q.caseSubQs.forEach(sq => {
              const m = Number(sq.marks ?? q.marks ?? 1) || 1;
              subjTotals[subject] = (subjTotals[subject] || 0) + m;
            });
          } else {
            const m = Number(q.marks ?? 1) || 1;
            subjTotals[subject] = (subjTotals[subject] || 0) + m;
          }
        });

        setQuestions(qs);
        setSubjectTotals(subjTotals);
        setExamTotal(total || 0);
      } catch {
        setQuestions([]); setSubjectTotals({}); setExamTotal(0);
      }
    })();
  }, [schoolId, examId]);

  /* -------- attempts (live) -------- */
  const [attempts, setAttempts] = useState([]);
  const [loadingAttempts, setLoadingAttempts] = useState(false);

  useEffect(() => {
    if (!schoolId || !teacher?.className) { setAttempts([]); return; }
    setLoadingAttempts(true);

    const ids = (examId === "" ? filteredExams.map(e => e.id) : [examId]).filter(Boolean);
    if (ids.length === 0) { setAttempts([]); setLoadingAttempts(false); return; }

    const perExamRows = new Map();
    const unsubs = [];
    const examNameById = new Map(exams.map(e => [e.id, e.name || e.id]));
    const examById     = new Map(exams.map(e => [e.id, e]));

    const attach = (exId) => {
      const base = collection(db, "schools", schoolId, "exams", exId, "attempts");
      const qref = query(base, qLimit(2000));

      const unsub = onSnapshot(
        qref,
        (snap) => {
          const rows = snap.docs.map(d => ({ id: d.id, _examId: exId, ...d.data() }));
          rows.sort((a, b) => (toDate(b.submittedAt) || 0) - (toDate(a.submittedAt) || 0));
          perExamRows.set(exId, rows);

          const merged = Array.from(perExamRows.values()).flat();

          const enriched = merged.map(r => {
            const sid = r.byStudent?.id || null;
            const uid = r.uid || r.id || r.byStudent?.uid || null;

            const st = (sid && studentById[sid]) || (uid && studentByUid[uid]) || null;
            const fromS = st ? {
              name: st.name, roll: st.rollNo, className: st.className, section: st.section, branch: st.branch
            } : {};

            const ex = examById.get(r._examId);
            const examType = String(ex?.examType || "").toLowerCase();
            const exClass   = pickFirst(ex?.className, ex?.class, ex?.standard, ex?.classId);
            const exSection = pickFirst(ex?.sectionKey, ex?.section, ex?.sec);

            const fromA = extractFromAttempt(r);

            return {
              ...r,
              examName: examNameById.get(r._examId) || r._examId,
              examType,
              isDescriptive: examType === "descriptive",
              studentName: pickFirst(r.studentName, r.name, fromA.name, fromS.name),
              roll:        pickFirst(r.roll, fromA.roll, fromS.roll),
              className:   pickFirst(r.className, fromA.className, fromS.className, exClass),
              section:     pickFirst(r.section,   fromA.section,   fromS.section,   exSection),
              branch:      pickFirst(r.branch,    fromA.branch,    fromS.branch),
              byStudent: {
                id: sid || st?.id,
                name: pickFirst(r.byStudent?.name, fromA.name, fromS.name),
                rollNo: pickFirst(r.byStudent?.rollNo, fromA.roll, fromS.roll),
                uid: uid || st?.uid || r.byStudent?.uid || undefined,
                ...r.byStudent
              }
            };
          });

          const cls = String(activeClass || "");
          const filteredForTeacher = enriched.filter(r => {
            if (String(r.className || "") !== cls) return false;
            if (teacherSectionsU.length === 0) return true;
            const rSecU = U(r.section || "");
            return rSecU === "" || teacherSectionsU.includes(rSecU);
          });

          setAttempts(filteredForTeacher);
          setLoadingAttempts(false);
        },
        () => setLoadingAttempts(false)
      );
      unsubs.push(unsub);
    };

    ids.forEach(attach);
    return () => unsubs.forEach(u => u && u());
  }, [schoolId, exams, examId, filteredExams, activeClass, teacherSectionsU.join("|"), studentById, studentByUid, teacher?.className]);

  /* -------- enrich & KPIs -------- */
  const attemptsEnriched = useMemo(() => {
    if (!attempts.length) return [];

    const qIdToSubject = new Map();
    questions.forEach(q => {
      const subject = q.subject || "General";
      const qt = String(q.questionType || "").toLowerCase();
      if (qt === "case_based" && Array.isArray(q.caseSubQs) && q.caseSubQs.length) {
        q.caseSubQs.forEach((sq, idx) => {
          const sid = sq?.id || `${q.id}__sub_${idx}`;
          qIdToSubject.set(sid, subject);
        });
      } else {
        qIdToSubject.set(q.id, subject);
      }
    });

    const readObtained = (a = {}) => {
      const v = a.evaluatedScore ?? a.score ?? a.marksObtained ?? a.obtained ?? a.points ?? null;
      const n = Number(v); return Number.isFinite(n) ? n : null;
    };
    const readTotalMarks = (a = {}) => {
      const v = a.totalMarks ?? a.maxMarks ?? a.fullMarks ?? a.total ?? a.outOf ?? null;
      const n = Number(v); return Number.isFinite(n) ? n : null;
    };
    const obtainedFromResults = (a = {}) => {
      if (!a || typeof a.resultsByQuestion !== "object") return null;
      try { return Object.values(a.resultsByQuestion).reduce((s, r) => s + (Number(r?.marks) || 0), 0); }
      catch { return null; }
    };

    const rows = attempts.map(a => {
      const obtained = readObtained(a) ?? obtainedFromResults(a) ?? 0;
      const total    = readTotalMarks(a) ?? (examId ? examTotal : null) ?? 0;
      const percent  = total > 0 ? (obtained / total) * 100 : null;

      const subjectStats = {};
      if (examId && a && a.resultsByQuestion && typeof a.resultsByQuestion === "object") {
        const sbj = {};
        Object.entries(a.resultsByQuestion).forEach(([qid, res]) => {
          const subject = qIdToSubject.get(qid);
          if (!subject) return;
          const marks = Number(res?.marks ?? 0) || 0;
          sbj[subject] = (sbj[subject] || 0) + marks;
        });
        Object.keys(subjectTotals).forEach(s => {
          const tot = subjectTotals[s] || 0;
          const ob  = sbj[s] ?? 0;
          subjectStats[s] = { obtained: ob, total: tot, percent: tot ? (ob / tot) * 100 : null, rank: null };
        });
      }

      return { ...a, obtained, total, percent, subjectStats };
    });

    const sorted = [...rows].sort((x, y) => {
      const px = Number.isFinite(x.percent) ? x.percent : -1;
      const py = Number.isFinite(y.percent) ? y.percent : -1;
      if (py !== px) return py - px;
      const tx = toDate(x.submittedAt)?.getTime() ?? Infinity;
      const ty = toDate(y.submittedAt)?.getTime() ?? Infinity;
      return tx - ty;
    });
    const idToRank = new Map(sorted.map((r, i) => [r.id, i + 1]));
    const N = sorted.length; const pctList = sorted.map(r => (Number.isFinite(r.percent) ? r.percent : -1));

    rows.forEach(r => {
      r.rank = idToRank.get(r.id) ?? "—";
      let below = 0; if (Number.isFinite(r.percent)) for (const v of pctList) if (v < r.percent) below++;
      r.percentile = N ? (below / N) * 100 : null;
    });

    if (examId) {
      Object.keys(subjectTotals).forEach(subject => {
        const arr = rows
          .map(r => ({ id: r.id, pct: r.subjectStats?.[subject]?.percent ?? null, t: toDate(r.submittedAt)?.getTime() ?? Infinity }))
          .sort((a, b) => {
            const pa = Number.isFinite(a.pct) ? a.pct : -1;
            const pb = Number.isFinite(b.pct) ? b.pct : -1;
            if (pb !== pa) return pb - pa;
            return a.t - b.t;
          });
        const rankMap = new Map(arr.map((x, i) => [x.id, i + 1]));
        rows.forEach(r => { if (r.subjectStats?.[subject]) r.subjectStats[subject].rank = rankMap.get(r.id) ?? "—"; });
      });
    }

    return rows;
  }, [attempts, examId, examTotal, subjectTotals, questions]);

  const [qText, setQText] = useState("");
  const [branch, setBranch] = useState("");
  const [hasPdf, setHasPdf] = useState(false);

  const branchOptions = useMemo(() => {
    const s = new Set(); attemptsEnriched.forEach(a => a.branch && s.add(String(a.branch))); return Array.from(s).sort();
  }, [attemptsEnriched]);

  const filtered = useMemo(() => {
    const text = lower(qText);
    return attemptsEnriched.filter(a => {
      if (text) {
        const nm = lower(a.studentName || a.name || a.byStudent?.name || "");
        const rl = lower(a.roll || a.byStudent?.rollNo || "");
        if (!nm.includes(text) && !rl.includes(text)) return false;
      }
      if (branch && String(a.branch || "") !== branch) return false;
      if (hasPdf) {
        const up = a.upload;
        if (!up || (!up.path && !up.url)) return false;
      }
      return true;
    });
  }, [attemptsEnriched, qText, branch, hasPdf]);

  const kpi = useMemo(() => {
    const n = filtered.length;
    const submitted = filtered.filter(r => r.status === "submitted" || r.status === "evaluated").length;
    const vals = filtered.map(r => (Number.isFinite(r.percent) ? r.percent : null)).filter(v => Number.isFinite(v));
    const avgPct = vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : null;
    let topper = null, bestPct = -1;
    filtered.forEach(r => { const p = Number.isFinite(r.percent) ? r.percent : null; if (p != null && p > bestPct) { bestPct = p; topper = r; } });
    return { count: n, submitted, avgPct, topper: topper ? { name: topper.studentName || topper.name || topper.byStudent?.name || "—", pct: Math.round(bestPct) } : null };
  }, [filtered]);

  const exportXLSX = () => {
    const subjKeys = Object.keys(subjectTotals);
    const rows = filtered.map(r => {
      const base = {
        Name: r.studentName || r.name || r.byStudent?.name || "",
        Roll: r.roll || r.byStudent?.rollNo || "",
        Class: r.className || "",
        Section: Array.isArray(r.section)? r.section.join(",") : String(r.section || ""),
        Branch: r.branch || "",
        Obtained: Number.isFinite(r.obtained) ? r.obtained : "",
        Total: r.total ?? "",
        Percent: Number.isFinite(r.percent) ? Math.round(r.percent) : "",
        Rank: r.rank ?? "",
        Percentile: Number.isFinite(r.percentile) ? Math.round(r.percentile) : "",
        Status: r.status || "",
        SubmittedAt: fmt(toDate(r.submittedAt)),
        HasPDF: r.upload && (r.upload.path || r.upload.url) ? "Yes" : "No",
        Exam: r.examName || r._examId || ""
      };
      subjKeys.forEach(s => {
        const st = r.subjectStats?.[s] || {};
        base[`${s} Obtained`] = Number.isFinite(st.obtained) ? Math.round(st.obtained * 100) / 100 : "";
        base[`${s} Total`]    = st.total ?? "";
        base[`${s} %`]        = Number.isFinite(st.percent) ? Math.round(st.percent) : "";
        base[`${s} Rank`]     = st.rank ?? "";
      });
      return base;
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), "Results");
    XLSX.writeFile(wb, `teacher_results_${activeClass || "class"}.xlsx`);
  };

  const { PdfModal } = usePdfViewer();
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRow, setDetailRow] = useState(null);
  const openDetail = (row) => { setDetailRow(row); setDetailOpen(true); };
  const closeDetail = () => setDetailOpen(false);

  const [evalOpen, setEvalOpen] = useState(false);
  const [evalRow, setEvalRow] = useState(null);
  const openEval = (row) => { setEvalRow(row); setEvalOpen(true); };
  const closeEval = () => setEvalOpen(false);

  if (!schoolId) {
    return <Container className="py-4"><Alert variant="warning">No school selected.</Alert></Container>;
  }
  if (!loadingTeacher && teacher && isInactive) {
    return (
      <Container fluid className="p-4">
        <Alert variant="danger" className="mb-3">
          <div className="fw-semibold mb-1">Access restricted</div>
          Your teacher account is marked as <b>Inactive</b>. Please contact the administrator.
        </Alert>
        <div className="d-flex gap-2">
          <Button variant="secondary" href="/">Go Home</Button>
          <Button variant="outline-secondary" href="/teacher/support">Contact Support</Button>
        </div>
      </Container>
    );
  }

  const nameBadge = (teacher?.primarySubject || activeClass || teacherSectionsU.length) ? (
    <div className="d-flex align-items-center gap-2 flex-wrap">
      {teacher?.primarySubject && (
        <span className="badge-soft-accent">
          <i className="bi bi-journal-text me-1" />
          {teacher.primarySubject}
        </span>
      )}
      {activeClass && <span className="badge-soft-accent">{activeClass}</span>}
      {teacherSectionsU.map((s) => (
        <span key={s} className="badge-soft-accent">Section {s}</span>
      ))}

      {teacherClasses.length > 1 && (
        <Form.Select
          size="sm"
          value={activeClass}
          onChange={(e) => setActiveClass(e.target.value)}
          style={{ width: 160 }}
          className="ms-2"
        >
          {teacherClasses.map(c => <option key={c} value={c}>{c}</option>)}
        </Form.Select>
      )}
    </div>
  ) : null;

  return (
    <Container fluid className="p-0">
      {/* Header */}
      <div className="tdb-hero mb-3">
        <div className="tdb-hero-inner">
          <div>
            <div className="tdb-eyebrow">Teacher • Student Results</div>
            <h5 className="mb-1 fw-bold">{teacherName}</h5>
            {nameBadge}
          </div>
          <div className="d-flex gap-2">
            <Button onClick={exportXLSX} className="btn-accent">
              <i className="bi bi-download me-1" /> Export XLSX
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
        <Form.Select size="sm" style={{ width: 160 }} value={testType} onChange={(e)=>setTestType(e.target.value)}>
          <option value="">All Test Types</option>
          {testTypeOptions.map(t => <option key={t} value={t}>{t}</option>)}
        </Form.Select>
<Form.Select
  size="sm"
  style={{ width: 160 }}
  value={examMode}
  onChange={(e) => setExamMode(e.target.value)}
>
  <option value="">All Modes</option>
  <option value="live">Live</option>
  <option value="mock">Mock</option>
</Form.Select>
        <Form.Select size="sm" style={{ width: 240 }} value={examId} onChange={(e)=>setExamId(e.target.value)} disabled={loadingExams}>
          <option value="">All Exams</option>
          {filteredExams.map(ex => <option key={ex.id} value={ex.id}>{ex.name || ex.id}</option>)}
        </Form.Select>

        <Form.Select size="sm" style={{ width: 180 }} value={branch} onChange={(e)=>setBranch(e.target.value)}>
          <option value="">All Branches</option>
          {branchOptions.map(b => <option key={b} value={b}>{b}</option>)}
        </Form.Select>

        <InputGroup size="sm" style={{ width: 240 }}>
          <InputGroup.Text>Search</InputGroup.Text>
          <Form.Control value={qText} onChange={(e)=>setQText(e.target.value)} placeholder="By name or roll" />
        </InputGroup>

        <Form.Check type="checkbox" label="Has PDF" checked={hasPdf} onChange={(e)=>setHasPdf(e.target.checked)} />

        <div className="ms-auto small text-muted">{loadingAttempts || loadingStudents ? "Loading…" : `${filtered.length} result(s)`}</div>
      </div>

      {/* KPIs */}
      <Row className="g-3 mb-3">
        <Col md={3}><Card className="kpi-card"><Card.Body><div className="small text-muted">Total Attempts</div><div className="fs-3 fw-bold">{kpi.count}</div></Card.Body></Card></Col>
        <Col md={3}><Card className="kpi-card"><Card.Body><div className="small text-muted">Submitted/Evaluated</div><div className="fs-3 fw-bold">{kpi.submitted}</div></Card.Body></Card></Col>
        <Col md={3}><Card className="kpi-card"><Card.Body><div className="small text-muted">Average %</div><div className="fs-3 fw-bold">{kpi.avgPct ?? "—"}</div></Card.Body></Card></Col>
        <Col md={3}><Card className="kpi-card"><Card.Body><div className="small text-muted">Topper</div><div className="fw-semibold">{kpi.topper ? `${kpi.topper.name} (${kpi.topper.pct}%)` : "—"}</div></Card.Body></Card></Col>
      </Row>

      {/* Subject leaderboards */}
      {examId && Object.keys(subjectTotals).length > 0 && (
        <Card className="soft-card mb-3">
          <Card.Header className="fw-semibold">Subject Leaderboards</Card.Header>
          <Card.Body>
            <Row className="g-3">
              {Object.keys(subjectTotals).map(subject => {
                const top = [...attemptsEnriched]
                  .filter(r => Number.isFinite(r.subjectStats?.[subject]?.percent))
                  .sort((a,b)=> (b.subjectStats[subject].percent - a.subjectStats[subject].percent))
                  .slice(0,3);
                return (
                  <Col md={4} key={subject}>
                    <div className="fw-semibold mb-2">{subject}</div>
                    {top.length === 0 ? (
                      <div className="text-muted small">No data</div>
                    ) : (
                      <ol className="mb-0 small">
                        {top.map(r => (
                          <li key={r.id}>{(r.studentName || r.name || r.byStudent?.name || "—")} — {Math.round(r.subjectStats[subject].percent)}%</li>
                        ))}
                      </ol>
                    )}
                  </Col>
                );
              })}
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* Results table — becomes stacked cards on mobile */}
      <Card className="soft-card">
        <Card.Header className="fw-semibold">Student Results</Card.Header>
        <Card.Body className="p-0">
          {loadingAttempts || loadingStudents ? (
            <div className="py-4 text-center"><Spinner animation="border" /></div>
          ) : filtered.length === 0 ? (
            <div className="py-4 text-center text-muted">No results found.</div>
          ) : (
            <div className="table-responsive">
              <Table className="mb-0 align-middle rb-table-stack">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th className="d-none d-md-table-cell">Roll</th>
                    <th>Exam</th>
                    <th className="text-center">Score</th>
                    <th className="text-center">%</th>
                    <th className="text-center">Rank</th>
                    <th className="text-center">Percentile</th>
                    <th className="text-center d-none d-lg-table-cell">Submitted</th>
                    <th className="text-center">PDF</th>
                    <th className="text-center">Details</th>
                    <th className="text-center">Evaluate</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => {
                    const name = r.studentName || r.name || r.byStudent?.name || "—";
                    const pct  = Number.isFinite(r.percent) ? Math.round(r.percent) : null;
                    const sections = Array.isArray(r.section)
                      ? r.section
                      : String(r.section || "").split(/[,\s/|]+/).filter(Boolean);
                    return (
                      <tr key={`${r._examId}-${r.id}`}>
                        <td data-label="Student">
                          <div className="d-flex align-items-center gap-2">
                            <div className="rounded-circle d-flex align-items-center justify-content-center"
                                 style={{ width: 34, height: 34, fontWeight: 700, fontSize: 11, ...avatarStyle(name) }}>
                              {initialsOf(name)}
                            </div>
                            <div className="min-w-0">
                              <div className="fw-semibold text-truncate">{name}</div>
                              <div className="small text-muted d-flex flex-wrap gap-1">
                                {r.branch && <span className="chip chip-info">{r.branch}</span>}
                                {r.className && <span className="chip">{r.className}</span>}
                                {sections.map((s) => <span key={s} className="chip">Sec {String(s).toUpperCase()}</span>)}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="d-none d-md-table-cell text-muted" data-label="Roll">
                          {r.roll || r.byStudent?.rollNo || "—"}
                        </td>
                     <td className="text-muted">
  {r.examName || r._examId || "—"}
  {(() => {
    const ex = exams.find(e => e.id === r._examId);
    const mode = ex ? getExamMode(ex) : "";
    return mode ? (
      <span className="chip ms-2" title="Exam Mode">
        {mode === "live" ? "Live" : mode === "mock" ? "Mock" : mode}
      </span>
    ) : null;
  })()}
</td>
                        <td data-label="Score" className="text-center">
                          {Number.isFinite(r.obtained) ? Math.round(r.obtained * 100)/100 : "—"} / {r.total ?? "—"}
                        </td>
                        <td data-label="%" className="text-center">{pct ?? "—"}%</td>
                        <td data-label="Rank" className="text-center">{r.rank ?? "—"}</td>
                        <td data-label="Percentile" className="text-center">{Number.isFinite(r.percentile) ? Math.round(r.percentile) : "—"}</td>
                        <td className="text-center d-none d-lg-table-cell" data-label="Submitted">
                          {fmt(toDate(r.submittedAt))}
                        </td>
                        <td data-label="PDF" className="text-center">
                          {r.upload ? (
                            <Button size="sm" variant="outline-secondary" onClick={() => window.open(r.upload.url || "", "_blank", "noopener")}>
                              Open
                            </Button>
                          ) : <span className="text-muted">—</span>}
                        </td>
                        <td data-label="Details" className="text-center">
                          <Button size="sm" onClick={() => openDetail(r)}>View</Button>
                        </td>
                        <td data-label="Evaluate" className="text-center">
                          {r.isDescriptive ? (
                            <Button
                              size="sm"
                              variant={Number.isFinite(r.obtained) ? "outline-primary" : "primary"}
                              onClick={() => openEval(r)}
                              disabled={!r.upload}
                              title={!r.upload ? "No PDF uploaded yet" : undefined}
                            >
                              {Number.isFinite(r.obtained) ? "Edit Marks" : "Evaluate"}
                            </Button>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Evaluate Modal */}
      <EvaluateDescriptiveModal
        show={evalOpen}
        onHide={closeEval}
        schoolId={schoolId}
        row={evalRow}
        teacher={teacher}
      />

      {/* Detail Modal */}
      <Modal show={detailOpen} onHide={closeDetail} size="xl" fullscreen="md-down">
        <Modal.Header closeButton><Modal.Title>Exam Analysis</Modal.Title></Modal.Header>
        <Modal.Body>
          {detailRow && (
            <EmbeddedQuestionAnalysis
              schoolId={schoolId}
              examId={examId || detailRow._examId}
              uid={detailRow?.uid || detailRow?.id || detailRow?.byStudent?.uid}
            />
          )}
        </Modal.Body>
      </Modal>

      {/* Local styles */}
      <style>{`
        .tdb-hero { border:1px solid var(--border); border-radius:16px;
          background:
            radial-gradient(60% 120% at 10% -20%, color-mix(in oklab, var(--brand-accent) 16%, transparent) 0%, transparent 60%),
            radial-gradient(40% 120% at 110% 10%, color-mix(in oklab, var(--brand-accent) 10%, transparent) 0%, transparent 60%),
            color-mix(in oklab, var(--surface) 96%, transparent);
        }
        .tdb-hero-inner { display:flex; align-items:center; justify-content:space-between; padding:16px 18px; border-radius:16px; gap:12px;}
        .tdb-eyebrow { font-size:12px; letter-spacing:.08em; text-transform:uppercase; color:var(--muted); }
        .soft-card, .kpi-card  { border-radius:16px; border:1px solid var(--border); background: color-mix(in oklab, var(--surface) 98%, transparent); }
        .badge-soft-accent{
          display:inline-block; padding:.25rem .55rem; border-radius:.6rem;
          border: 1px solid color-mix(in oklab, var(--brand-accent) 35%, var(--border));
          background: color-mix(in oklab, var(--brand-accent) 10%, transparent);
          color: var(--brand-accent); font-weight: 600; font-size:.82rem;
        }
        .btn-accent{
          --_c: var(--brand-accent, #0d6efd);
          border-color: var(--_c);
          background: radial-gradient(120% 180% at 0% 0%, var(--_c), color-mix(in oklab, var(--_c) 18%, transparent));
          color:#fff;
          box-shadow:0 6px 18px color-mix(in oklab, var(--_c) 35%, transparent);
        }
        .chip{ display:inline-block; padding:.1rem .4rem; border-radius:.4rem; border:1px solid var(--border); background: color-mix(in oklab, var(--surface) 96%, transparent); }
        .chip-info{ border-color: color-mix(in oklab, var(--brand-accent) 35%, var(--border)); color: var(--brand-accent); }

        /* ===== Mobile stacked table ===== */
        @media (max-width: 576px) {
          .rb-table-stack thead {
            display: none;
          }
          .rb-table-stack tbody tr {
            display: block;
            border-top: 1px solid var(--border);
            padding: .5rem .5rem;
          }
          .rb-table-stack tbody tr:first-child { border-top: 0; }
          .rb-table-stack tbody td {
            display: grid;
            grid-template-columns: 35% 1fr;
            gap: .25rem .75rem;
            padding: .35rem .25rem !important;
            border: 0 !important;
          }
          .rb-table-stack tbody td::before {
            content: attr(data-label);
            font-weight: 600;
            color: var(--muted);
            text-transform: none;
          }
          .rb-table-stack tbody td[data-label="Student"] {
            grid-template-columns: 1fr;
          }
          .rb-table-stack tbody td[data-label="Student"]::before {
            content: "";
          }
        }
      `}</style>
    </Container>
  );
}
